import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiService } from '../services/api';
import { AuthService } from '../services/auth';
import { Session, Question } from '../types';
import { useSocket } from '../hooks/useSocket';
import ImportQuestions from '../components/ImportQuestions';
import { TeacherSessionStats } from '../components/SessionStatistics';
import { StudentRanking } from '../components/StudentRanking';
import { MarkdownFeedback } from '../components/MarkdownFeedback';
import { ShareSessionModal } from '../components/ShareSessionModal';
import { evaluationApi, EvaluationStats } from '../services/evaluationApi';
import { statsApi, SessionStats } from '../services/statsApi';
import '../styles/evaluation.css';

const TeacherSessionManagement: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teacher, setTeacher] = useState<any>(null);
  const [responseStats, setResponseStats] = useState<any>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  // const [evaluationStatus, setEvaluationStatus] = useState<{configured: boolean, message: string} | null>(null);
  const [evaluationStats, setEvaluationStats] = useState<EvaluationStats | null>(null);
  const [evaluatingQuestions, setEvaluatingQuestions] = useState<Set<string>>(new Set());
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const { socket } = useSocket();

  // Fonction pour exporter le classement en CSV
  // Fonction utilitaire pour supprimer les doublons (garder la réponse la plus récente)
  const removeDuplicateResponses = (responses: any[]) => {
    const studentMap = new Map();
    
    responses.forEach(response => {
      const studentId = response.studentId;
      if (!studentMap.has(studentId)) {
        studentMap.set(studentId, []);
      }
      studentMap.get(studentId).push(response);
    });
    
    const cleanedResponses: any[] = [];
    studentMap.forEach((studentResponses, studentId) => {
      studentResponses.sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      cleanedResponses.push(studentResponses[0]);
    });
    
    return cleanedResponses;
  };

  const exportRankingToCSV = () => {
    if (!session || !questions) return;

    // Calculer les statistiques des étudiants sans doublons
    const studentMap = new Map();
    
    if (session.participants) {
      session.participants.forEach((participant: any) => {
        studentMap.set(participant.studentId, {
          studentId: participant.studentId,
          studentName: participant.studentName || `Étudiant ${participant.studentId}`,
          totalResponses: 0,
          correctResponses: 0,
          accuracyRate: 0,
          participationRate: 0,
          rank: 0,
          openEndedScore: 0,
          totalOpenEndedResponses: 0
        });
      });
    }
    
    // Filtrer les questions qui ont une bonne réponse définie (QCM et Vrai/Faux)
    const questionsWithCorrectAnswer = questions.filter(question => 
      question.correctAnswer !== undefined && 
      question.correctAnswer !== null && 
      question.correctAnswer !== '' &&
      (question.type === 'multiple_choice' || question.type === 'true_false')
    );
    
    // Filtrer les questions ouvertes évaluées
    const evaluatedOpenEndedQuestions = questions.filter(question => 
      question.type === 'open_ended' && 
      question.responses && 
      question.responses.some((r: any) => r.concordanceScore !== null && r.concordanceScore !== undefined)
    );
    
    // Analyser les réponses de toutes les questions (pour la participation) - sans doublons
    questions.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats) {
            studentStats.totalResponses++;
          }
        });
      }
    });
    
    // Analyser les réponses correctes (QCM/VF + Questions ouvertes évaluées) - sans doublons
    questionsWithCorrectAnswer.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats) {
            // Vérifier si la réponse est correcte pour QCM/VF
            if (question.type === 'multiple_choice' || question.type === 'true_false') {
              const responseIndex = parseInt(response.answer);
              const correctIndex = parseInt(question.correctAnswer || '');
              if (responseIndex === correctIndex) {
                studentStats.correctResponses++;
              }
            }
          }
        });
      }
    });
    
    // Analyser les questions ouvertes évaluées - sans doublons
    evaluatedOpenEndedQuestions.forEach(question => {
      if (question.responses && question.responses.length > 0) {
        const cleanedResponses = removeDuplicateResponses(question.responses);
        cleanedResponses.forEach((response: any) => {
          const studentStats = studentMap.get(response.studentId);
          if (studentStats && response.concordanceScore !== null && response.concordanceScore !== undefined) {
            // Ajouter le score de concordance au score total des questions ouvertes
            studentStats.openEndedScore += response.concordanceScore;
            studentStats.totalOpenEndedResponses++;
            
            // Considérer qu'un score >= 50% est une "réponse correcte" pour le classement
            if (response.concordanceScore >= 50) {
              studentStats.correctResponses++;
            }
          }
        });
      }
    });
    
    // Calculer les taux et classer
    const studentStatsArray = Array.from(studentMap.values()).map(stats => {
      // Taux de participation (réponses / nombre total de questions)
      stats.participationRate = questions.length > 0 ? (stats.totalResponses / questions.length) * 100 : 0;
      
      // Taux de réussite global (QCM/VF + Questions ouvertes) - sans doublons
      const totalEvaluableResponses = questionsWithCorrectAnswer.reduce((total, question) => {
        const cleanedResponses = removeDuplicateResponses(question.responses || []);
        const studentResponses = cleanedResponses.filter((r: any) => r.studentId === stats.studentId);
        return total + studentResponses.length;
      }, 0) + evaluatedOpenEndedQuestions.reduce((total, question) => {
        const cleanedResponses = removeDuplicateResponses(question.responses || []);
        const studentResponses = cleanedResponses.filter((r: any) => r.studentId === stats.studentId && r.concordanceScore !== null);
        return total + studentResponses.length;
      }, 0);
      
      stats.accuracyRate = totalEvaluableResponses > 0 ? (stats.correctResponses / totalEvaluableResponses) * 100 : 0;
      return stats;
    });
    
    // Classer par performance
    studentStatsArray.sort((a, b) => {
      if (Math.abs(a.accuracyRate - b.accuracyRate) > 0.1) {
        return b.accuracyRate - a.accuracyRate;
      }
      if (Math.abs(a.participationRate - b.participationRate) > 0.1) {
        return b.participationRate - a.participationRate;
      }
      return b.totalResponses - a.totalResponses;
    });
    
    // Assigner les rangs
    studentStatsArray.forEach((stats, index) => {
      stats.rank = index + 1;
    });

    // Créer le contenu CSV avec plus de détails
    const csvHeaders = [
      'Rang',
      'Nom de l\'étudiant',
      'ID Étudiant',
      'Total Réponses',
      'Réponses Correctes',
      'Taux de Participation (%)',
      'Taux de Réussite (%)',
      'Questions Ouvertes Évaluées',
      'Moyenne Questions Ouvertes (%)'
    ];
    
    const csvRows = studentStatsArray.map(student => [
      student.rank,
      `"${student.studentName}"`,
      student.studentId,
      student.totalResponses,
      student.correctResponses,
      Math.round(student.participationRate),
      Math.round(student.accuracyRate),
      student.totalOpenEndedResponses,
      student.totalOpenEndedResponses > 0 ? Math.round(student.openEndedScore / student.totalOpenEndedResponses) : 0
    ]);
    
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');
    
    // Créer et télécharger le fichier
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `classement_session_${session.name || session.id}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour exporter les questions en JSON
  const exportQuestionsToJSON = async () => {
    if (!sessionId) return;

    try {
      const blob = await ApiService.exportQuestions(sessionId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `questions-${session?.name || 'session'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export des questions:', error);
      alert('Erreur lors de l\'export des questions');
    }
  };

  // Fonction pour calculer les statistiques des réponses (pour tous les types de questions)
  const calculateResponseStats = (question: Question) => {
    if (!question.responses || question.responses.length === 0) {
      return null;
    }

    const stats: any = {
      totalResponses: question.responses.length,
      optionCounts: {},
      correctCount: 0,
      incorrectCount: 0
    };

    // Compter les réponses selon le type de question
    question.responses.forEach(response => {
      const answer = response.answer;
      
      if (question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'investigation') {
        // Pour les QCM, Vrai/Faux et investigations, compter par option
        const optionText = question.options && question.options[parseInt(answer)] 
          ? question.options[parseInt(answer)].text 
          : `Option ${answer}`;
        
        stats.optionCounts[optionText] = (stats.optionCounts[optionText] || 0) + 1;

        // Pour les investigations, on ne calcule pas de correct/incorrect
        if (question.type === 'investigation') {
          stats.correctCount = undefined;
          stats.incorrectCount = undefined;
        } else if (question.correctAnswer && question.correctAnswer !== '') {
          // Compter correct/incorrect seulement si une bonne réponse est définie
          if (answer === question.correctAnswer) {
            stats.correctCount++;
          } else {
            stats.incorrectCount++;
          }
        }
      } else if (question.type === 'rating') {
        // Pour les questions rating, utiliser les labels ou les valeurs numériques
        const ratingValue = parseInt(answer);
        const labelText = question.ratingScale?.labels?.[ratingValue - question.ratingScale.min] || answer;
        
        stats.optionCounts[labelText] = (stats.optionCounts[labelText] || 0) + 1;
        
        // Pas de comptage correct/incorrect pour les questions rating
        stats.correctCount = undefined;
        stats.incorrectCount = undefined;
      } else if (question.type === 'open_ended') {
        // Pour les questions ouvertes, afficher la distribution des scores de concordance
        const response = question.responses.find(r => r.answer === answer);
        if (response && response.concordanceScore !== null && response.concordanceScore !== undefined) {
          // Catégoriser les scores
          let category = '';
          if (response.concordanceScore >= 90) {
            category = 'Excellent (90-100%)';
          } else if (response.concordanceScore >= 70) {
            category = 'Bon (70-89%)';
          } else if (response.concordanceScore >= 50) {
            category = 'Moyen (50-69%)';
          } else {
            category = 'Insuffisant (0-49%)';
          }
          stats.optionCounts[category] = (stats.optionCounts[category] || 0) + 1;
        } else {
          // Réponse non évaluée
          stats.optionCounts['Non évaluée'] = (stats.optionCounts['Non évaluée'] || 0) + 1;
        }
        
        // Pas de comptage correct/incorrect pour les questions ouvertes
        stats.correctCount = undefined;
        stats.incorrectCount = undefined;
      }
    });

    return stats;
  };

  // Charger le statut de configuration du service d'évaluation
  // useEffect(() => {
  //   const loadEvaluationStatus = async () => {
  //     try {
  //       const status = await evaluationApi.getEvaluationStatus();
  //       setEvaluationStatus(status);
  //     } catch (error) {
  //       console.error('Erreur lors du chargement du statut d\'évaluation:', error);
  //       setEvaluationStatus({ configured: false, message: 'Erreur lors du chargement du statut' });
  //     }
  //   };

  //   loadEvaluationStatus();
  // }, []);

  // Charger les statistiques d'évaluation pour la question active (questions ouvertes)
  useEffect(() => {
    const loadEvaluationStats = async () => {
      if (session?.currentQuestion?.type === 'open_ended' && session.currentQuestion.id) {
        try {
          const stats = await evaluationApi.getEvaluationStats(session.currentQuestion.id);
          setEvaluationStats(stats);
        } catch (error) {
          console.error('Erreur lors du chargement des statistiques d\'évaluation:', error);
          setEvaluationStats(null);
        }
      } else {
        setEvaluationStats(null);
      }
    };

    loadEvaluationStats();
  }, [session?.currentQuestion]);

  // Fonction pour évaluer toutes les réponses d'une question ouverte
  const handleEvaluateAllResponses = async (questionId: string) => {
    setEvaluatingQuestions(prev => new Set(prev).add(questionId));
    
    try {
      const result = await evaluationApi.evaluateAllResponses(questionId);
      
      if (result.success) {
        // Recharger les données de la session pour mettre à jour les évaluations
        await loadSessionData();
        
        alert(`Évaluation terminée: ${result.evaluatedCount} réponses évaluées avec succès${result.errorCount > 0 ? `, ${result.errorCount} erreurs` : ''}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'évaluation:', error);
      alert(`Erreur lors de l'évaluation: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setEvaluatingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    // Vérifier si un enseignant est connecté
    const currentTeacher = AuthService.getTeacher();
    if (!currentTeacher) {
      navigate('/teacher/login');
      return;
    }
    
    setTeacher(currentTeacher);
    if (sessionId) {
      loadSessionData();
    }
  }, [sessionId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les statistiques de session sans doublons
  useEffect(() => {
    const loadSessionStats = async () => {
      if (session?.id) {
        try {
          const stats = await statsApi.getSessionStats(session.id);
          setSessionStats(stats.stats);
          console.log('📊 Statistiques de session chargées:', stats.stats);
        } catch (error) {
          console.error('Erreur lors du chargement des statistiques de session:', error);
          setSessionStats(null);
        }
      }
    };

    loadSessionStats();
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Écouter les événements Socket.io pour les mises à jour en temps réel
  useEffect(() => {
    if (!socket || !session) return;

    // Rejoindre la session en tant qu'enseignant
    socket.joinSession(session.code, 'teacher', teacher?.id, teacher?.name);

    // Écouter les événements
    const handleUserJoined = (data: any) => {
      console.log('👤 Étudiant connecté:', data);
      console.log('👤 Nouveau nombre de participants:', data.participantsCount);
      // Mettre à jour uniquement le nombre de participants sans recharger
      setSession(prevSession => {
        if (!prevSession) return prevSession;
        return {
          ...prevSession,
          participantsCount: data.participantsCount || prevSession.participantsCount + 1
        };
      });
      
      // Recharger les statistiques de l'enseignant (nouvel étudiant)
      reloadTeacherStats();
    };

    const handleAnswerSubmitted = (data: any) => {
      console.log('✅ Réponse reçue:', data);
      // Mettre à jour uniquement les réponses de la question concernée
      if (data.questionId && session) {
        // Recharger uniquement cette question depuis l'API sans recharger toute la session
        ApiService.getQuestion(data.questionId).then(response => {
          if (response.success) {
            // Mettre à jour la question spécifique
            setQuestions(prev => prev.map(q => 
              q.id === data.questionId ? response.question : q
            ));
            
            // Recalculer les stats si c'est la question active
            if (session.currentQuestion && session.currentQuestion.id === data.questionId) {
              const stats = calculateResponseStats(response.question);
              setResponseStats(stats);
              console.log('📊 Stats mises à jour pour question active:', stats);
            }
          }
        }).catch(err => console.error('Erreur rechargement question:', err));
      }
    };

    // Ajouter les écouteurs d'événements
    socket.on('user-joined', handleUserJoined);
    socket.on('answer-submitted', handleAnswerSubmitted);

    // Nettoyage
    return () => {
      socket.off('user-joined', handleUserJoined);
      socket.off('answer-submitted', handleAnswerSubmitted);
      socket.leaveSession();
    };
  }, [socket, session, teacher]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction pour recharger les statistiques de l'enseignant
  const reloadTeacherStats = async () => {
    if (!teacher) return;
    
    try {
      const teacherResponse = await AuthService.getTeacherInfo(teacher.id);
      if (teacherResponse.success) {
        setTeacher(teacherResponse.teacher);
        console.log('✅ Stats enseignant rechargées:', teacherResponse.teacher);
      }
    } catch (error) {
      console.error('Erreur rechargement stats enseignant:', error);
    }
  };

  const exportSessionResults = async () => {
    if (!sessionId || !session) return;
    
    try {
      setError(null);
      
      // Récupérer les données d'export
      const blob = await ApiService.exportSessionResults(sessionId);
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nom du fichier basé sur le code de session et la date
      const today = new Date().toISOString().split('T')[0];
      link.download = `session-${session.code}-results-${today}.json`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Export des résultats réussi');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'export des résultats');
    }
  };

  // Fonction pour recharger les questions après import
  const handleImportSuccess = async () => {
    if (!sessionId) return;
    
    try {
      const response = await ApiService.getSessionQuestions(sessionId);
      if (response.success) {
        setQuestions(response.questions);
        console.log('✅ Questions rechargées après import');
      }
    } catch (err) {
      console.error('Erreur lors du rechargement des questions:', err);
    }
  };

  // Fonction pour supprimer une question
  const handleDeleteQuestion = async (questionId: string, questionTitle: string) => {
    if (!questionId) return;

    // Confirmation avant suppression
    const confirmMessage = `Êtes-vous sûr de vouloir supprimer la question "${questionTitle}" ?\n\nCette action est irréversible et supprimera également toutes les réponses associées.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError(null);
      
      const response = await ApiService.deleteQuestion(questionId);
      
      if (response.success) {
        console.log('✅ Question supprimée:', questionTitle);
        
        // Recharger les questions
        await handleImportSuccess();
        
        // Si la question supprimée était active, mettre à jour la session
        if (session?.currentQuestion?.id === questionId && sessionId) {
          const updatedSession = await ApiService.getSession(sessionId);
          if (updatedSession.success) {
            setSession(updatedSession.session);
          }
        }
      } else {
        setError(response.message || 'Erreur lors de la suppression de la question');
      }
    } catch (err: any) {
      console.error('Erreur suppression question:', err);
      setError(err.message || 'Erreur lors de la suppression de la question');
    }
  };

  // Fonction pour mettre à jour le statut de la session
  const updateSessionStatus = async (newStatus: string) => {
    if (!sessionId || !session) return;
    
    try {
      setError(null);
      const response = await ApiService.updateSessionStatus(sessionId, newStatus);
      
      if (response.success) {
        // Mettre à jour le statut local
        setSession(prevSession => {
          if (!prevSession) return prevSession;
          return {
            ...prevSession,
            status: newStatus as 'created' | 'active' | 'paused' | 'ended'
          };
        });
        console.log(`✅ Session ${newStatus === 'active' ? 'démarrée' : newStatus === 'paused' ? 'mise en pause' : 'arrêtée'}`);
      } else {
        setError('Erreur lors de la mise à jour du statut de la session');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour du statut de la session');
    }
  };

  const loadSessionData = async (preserveScroll = false) => {
    if (!sessionId) return;
    
    try {
      // Préserver la position de scroll si demandé
      const scrollPosition = preserveScroll ? window.scrollY : 0;
      
      if (!preserveScroll) {
        setLoading(true);
      }
      setError(null);
      
      // Charger les données de la session
      const [sessionResponse] = await Promise.all([
        ApiService.getSession(sessionId)
      ]);
      
      if (sessionResponse.success) {
        setSession(sessionResponse.session);
        console.log('📊 Session rechargée:', sessionResponse.session);
        console.log('📊 Nombre de participants:', sessionResponse.session.participantsCount);
        setQuestions(sessionResponse.session.questions);
        
        // Debug: Vérifier les données collectées
        console.log('🔍 Debug - Données collectées:', {
          questions: sessionResponse.session.questions,
          participantsCount: sessionResponse.session.participantsCount
        });
        
        // Calculer les statistiques pour la question active
        const activeQuestion = sessionResponse.session.currentQuestion;
        console.log('🔍 Debug - Question active:', activeQuestion);
        if (activeQuestion) {
          const stats = calculateResponseStats(activeQuestion);
          console.log('📊 Debug - Statistiques calculées:', stats);
          setResponseStats(stats);
        } else {
          console.log('❌ Debug - Aucune question active');
          setResponseStats(null);
        }
        
        // Restaurer la position de scroll si elle était préservée
        if (preserveScroll && scrollPosition > 0) {
          setTimeout(() => {
            window.scrollTo(0, scrollPosition);
          }, 0);
        }
      } else {
        setError('Erreur lors du chargement de la session');
      }
      
    //   if (questionsResponse.success) {
    //     console.log('questionsResponse.questions', questionsResponse.questions);
    //     setQuestions(questionsResponse.questions);
    //   } else {
    //     setError('Erreur lors du chargement des questions');
    //   }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      if (!preserveScroll) {
        setLoading(false);
      }
    }
  };

  const handleLogout = () => {
    AuthService.logoutTeacher();
    navigate('/');
  };

  const activateQuestion = async (questionId: string) => {
    if (!socket) return;
    setError(null);
    console.log('activateQuestion', questionId);
    
    try {
      // Activer la question via l'API
      const response = await ApiService.activateQuestion(questionId);
      
      if (response.success) {
        // Recharger les données de session pour mettre à jour l'indicateur (sans scroll)
        await loadSessionData(true);
        console.log('✅ Question activée et données rechargées');
      } else {
        setError('Erreur lors de l\'activation de la question');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'activation de la question');
    }
  };

  const activateNextQuestion = async () => {
    if (!questions || questions.length === 0) {
      setError('Aucune question disponible');
      return;
    }

    // Trouver la question actuellement active
    const currentActiveQuestion = questions.find(q => q.isActive);
    
    if (!currentActiveQuestion) {
      // Si aucune question n'est active, activer la première
      await activateQuestion(questions[0].id);
    } else {
      // Trouver l'index de la question active
      const currentIndex = questions.findIndex(q => q.id === currentActiveQuestion.id);
      
      if (currentIndex < questions.length - 1) {
        // Activer la question suivante
        await activateQuestion(questions[currentIndex + 1].id);
      } else {
        setError('Aucune question suivante disponible');
      }
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Gestion de Session</h1>
            {session && !(session as Session).isShared && (
              <button
                onClick={() => setShowShareModal(true)}
                className="btn btn-secondary"
                style={{ marginLeft: '1rem' }}
                title="Partager cette session avec un autre enseignant"
              >
                🔗 Partager
              </button>
            )}
            {session && (session as Session).isShared && (
              <span className="badge" style={{ marginLeft: '1rem', backgroundColor: '#17a2b8', color: 'white', padding: '0.5rem 1rem' }}>
                Session partagée
              </span>
            )}
            <div className="header-actions">
              {teacher && (
                <div className="teacher-info">
                  <span className="teacher-name">
                    <i className="fas fa-user"></i>
                    {teacher.name}
                  </span>
                  <span className="teacher-stats">
                    {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                  </span>
                </div>
              )}
              <button onClick={handleLogout} className="btn btn-outline btn-sm">
                <i className="fas fa-sign-out-alt"></i>
                Déconnexion
              </button>
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Gestion de Session</h1>
            <div className="header-actions">
              {session && !(session as Session).isShared && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="btn btn-secondary btn-sm"
                  title="Partager cette session avec un autre enseignant"
                >
                  🔗 Partager{(session as Session).sharedWith && (session as Session).sharedWith!.length > 0 ? ` (${(session as Session).sharedWith!.length})` : ''}
                </button>
              )}
              {/* Pour les sessions partagées avec l'utilisateur (isShared = true), afficher un badge différent */}
              {session && (session as Session).isShared && (
                <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', padding: '0.5rem 1rem', borderRadius: '4px' }}>
                  Session reçue
                </span>
              )}
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="card">
            <div className="card-body text-center">
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
              <button onClick={() => navigate('/teacher/dashboard')} className="btn btn-primary btn-sm">
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Gestion de Session</h1>
            <div className="header-actions">
              {session && !(session as Session).isShared && (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="btn btn-secondary btn-sm"
                  title="Partager cette session avec un autre enseignant"
                >
                  🔗 Partager{(session as Session).sharedWith && (session as Session).sharedWith!.length > 0 ? ` (${(session as Session).sharedWith!.length})` : ''}
                </button>
              )}
              {/* Pour les sessions partagées avec l'utilisateur (isShared = true), afficher un badge différent */}
              {session && (session as Session).isShared && (
                <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', padding: '0.5rem 1rem', borderRadius: '4px' }}>
                  Session reçue
                </span>
              )}
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="card">
            <div className="card-body text-center">
              <p>Session non trouvée</p>
              <button onClick={() => navigate('/teacher/dashboard')} className="btn btn-primary btn-sm">
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">Gestion de Session</h1>
          <div className="header-actions">
            {teacher && (
              <div className="teacher-info">
                <span className="teacher-name">
                  <i className="fas fa-user"></i>
                  {teacher.name}
                </span>
                <span className="teacher-stats">
                  {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                </span>
              </div>
            )}
            <button onClick={handleLogout} className="btn btn-outline btn-sm">
              <i className="fas fa-sign-out-alt"></i>
              Déconnexion
            </button>
            <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
              ← Tableau de bord
            </Link>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Gestion de Session</h2>
            <div className="flex gap-2 align-center">
              <span className={`status-badge ${session.status}`}>
                {session.status === 'created' && 'Créée'}
                {session.status === 'active' && 'Active'}
                {session.status === 'paused' && 'En pause'}
                {session.status === 'ended' && 'Terminée'}
              </span>
              
              {/* Boutons d'action principaux */}
              <div className="flex gap-1">
                {session.status === 'created' && (
                  <button
                    onClick={() => updateSessionStatus('active')}
                    className="btn btn-success btn-sm"
                  >
                    Démarrer
                  </button>
                )}
                
                {session.status === 'active' && (
                  <>
                    <Link 
                      to={`/teacher/sessions/${session.id}/live`}
                      className="btn btn-primary btn-sm"
                    >
                      Live
                    </Link>
                    <button
                      onClick={() => updateSessionStatus('paused')}
                      className="btn btn-warning btn-sm"
                    >
                      Pause
                    </button>
                    <button
                      onClick={() => updateSessionStatus('ended')}
                      className="btn btn-danger btn-sm"
                    >
                      Arrêter
                    </button>
                  </>
                )}
                
                {session.status === 'paused' && (
                  <>
                    <button
                      onClick={() => updateSessionStatus('active')}
                      className="btn btn-success btn-sm"
                    >
                      Reprendre
                    </button>
                    <button
                      onClick={() => updateSessionStatus('ended')}
                      className="btn btn-danger btn-sm"
                    >
                      Arrêter
                    </button>
                  </>
                )}
                
                {session.status === 'ended' && (
                  <>
                    <button
                      onClick={() => setShowStatistics(!showStatistics)}
                      className="btn btn-info btn-sm"
                    >
                      {showStatistics ? '📊 Masquer Stats' : '📊 Voir Stats'}
                    </button>
                    <button
                      onClick={() => setShowRanking(!showRanking)}
                      className="btn btn-warning btn-sm"
                    >
                      {showRanking ? '🏆 Masquer Classement' : '🏆 Voir Classement'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="card-body">
            <div className="grid grid-2">
              <div>
                <h3>Informations de la Session</h3>
                <div className="session-info">
                  <p><strong>Nom:</strong> {session.name}</p>
                  <p><strong>Code:</strong> <code>{session.code}</code></p>
                  <p><strong>Statut:</strong> {session.status}</p>
                  <p><strong>Participants:</strong> {session.participantsCount}</p>
                  <p><strong>Questions:</strong> {questions.length}</p>
                  <p><strong>Mode:</strong> {session.isAutonomous ? '🔄 Autonome' : '👨‍🏫 Supervisé'}</p>
                  <p><strong>Timer:</strong> {session.disableTimer ? '⏸️ Désactivé' : '⏰ Activé'}</p>
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <button
                      onClick={async () => {
                        try {
                          const newMode = !session.isAutonomous;
                          const response = await ApiService.setSessionAutonomous(session.id, newMode);
                          if (response.success) {
                            setSession({ ...session, isAutonomous: newMode });
                            alert(`Session ${newMode ? 'activée' : 'désactivée'} en mode autonome`);
                          }
                        } catch (error) {
                          console.error('Erreur changement mode:', error);
                          alert('Erreur lors du changement de mode');
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: session.isAutonomous ? '#ff9800' : '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      {session.isAutonomous ? '👨‍🏫 Passer en mode Supervisé' : '🔄 Passer en mode Autonome'}
                    </button>
                  </div>
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <button
                      onClick={async () => {
                        try {
                          const newTimerStatus = !session.disableTimer;
                          const response = await ApiService.setSessionTimer(session.id, newTimerStatus);
                          if (response.success) {
                            setSession({ ...session, disableTimer: newTimerStatus });
                            alert(`Timer ${newTimerStatus ? 'désactivé' : 'activé'} pour cette session`);
                          }
                        } catch (error) {
                          console.error('Erreur changement timer:', error);
                          alert('Erreur lors du changement du timer');
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: session.disableTimer ? '#ff9800' : '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      {session.disableTimer ? '⏰ Activer le timer' : '⏸️ Désactiver le timer'}
                    </button>
                  </div>
                  <p><strong>Créée le:</strong> {new Date(session.createdAt).toLocaleString()}</p>
                  <div style={{ marginTop: '15px', padding: '10px', background: session.isAutonomous ? '#e8f5e8' : '#e3f2fd', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: session.isAutonomous ? '#2e7d32' : '#1976d2' }}>
                      {session.isAutonomous ? '🔄 Lien de participation autonome' : '📤 Lien de participation'}
                    </p>
                    <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#424242' }}>
                      {session.isAutonomous 
                        ? 'Les étudiants peuvent répondre aux questions en autonomie sans activation manuelle'
                        : 'Partagez ce lien avec vos étudiants pour qu\'ils puissent rejoindre la session'
                      }
                    </p>
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/quiz/${session.code}`}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '0.9em',
                        background: 'white',
                        cursor: 'text'
                      }}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3>Gestion des Questions</h3>
                <div className="flex flex-col gap-2">
                  <Link
                    to={`/teacher/sessions/${session.id}/questions/create`}
                    className="btn btn-outline btn-sm"
                  >
                    ➕ Ajouter une Question
                  </Link>
                  
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="btn btn-outline btn-sm"
                  >
                    📥 Importer depuis JSON
                  </button>
                  
                  <button
                    onClick={exportQuestionsToJSON}
                    className="btn btn-outline btn-sm"
                    disabled={!questions || questions.length === 0}
                  >
                    📤 Exporter vers JSON
                  </button>
                  
                  <button
                    onClick={exportSessionResults}
                    className="btn btn-info btn-sm"
                    disabled={questions.length === 0}
                  >
                    📊 Exporter les Résultats
                  </button>
                </div>
              </div>
            </div>
            
            {/* Bouton Suivant en bas de la carte */}
            <div className="card-footer">
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={activateNextQuestion}
                  className="btn btn-primary btn-sm"
                  disabled={questions.length === 0}
                >
                  Suivant →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Histogramme des réponses pour la question active */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📊 Statistiques des Réponses</h3>
              <span className="status-badge status-active">
                {session.currentQuestion?.title}
              </span>
            </div>
            <div className="card-body">
              <div className="response-histogram">
                {responseStats ? (
                  <>
                    <div className="histogram-stats">
                      <div className="stat-item">
                        <span className="stat-label">Total des réponses:</span>
                        <span className="stat-value">{responseStats.totalResponses}</span>
                      </div>
                      {responseStats.correctCount !== undefined && (
                        <>
                          <div className="stat-item">
                            <span className="stat-label">Correctes:</span>
                            <span className="stat-value correct">{responseStats.correctCount}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Incorrectes:</span>
                            <span className="stat-value incorrect">{responseStats.incorrectCount}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="histogram-bars">
                      {Object.entries(responseStats.optionCounts).map(([option, count]) => {
                        const percentage = (count as number / responseStats.totalResponses) * 100;
                        const questionType = session.currentQuestion?.type;
                        return (
                          <div key={option} className="histogram-bar-container">
                            <div 
                              className="histogram-label"
                              style={{
                                wordBreak: questionType === 'open_ended' ? 'break-word' : 'normal',
                                overflow: questionType === 'open_ended' ? 'hidden' : 'visible',
                                maxWidth: questionType === 'open_ended' ? '300px' : 'none',
                                fontSize: questionType === 'open_ended' ? '0.9em' : '1em',
                                lineHeight: questionType === 'open_ended' ? '1.3' : 'normal'
                              }}
                              title={questionType === 'open_ended' ? option : undefined}
                            >
                              {option}
                            </div>
                            <div className="histogram-bar">
                              <div 
                                className="histogram-bar-fill"
                                style={{ 
                                  width: `${percentage}%`,
                                  backgroundColor: questionType === 'rating' ? '#007bff' : 
                                                 questionType === 'open_ended' ? '#17a2b8' :
                                                 option === session.currentQuestion?.correctAnswer ? '#28a745' : '#6c757d'
                                }}
                              >
                                <span className="histogram-count">{count as number}</span>
                              </div>
                            </div>
                            <div className="histogram-percentage">
                              {percentage.toFixed(1)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="no-stats">
                    <p>Aucune statistique disponible pour cette question.</p>
                  </div>
                )}
              </div>
              
              {/* Section d'évaluation automatique pour les questions ouvertes */}
              {session.currentQuestion?.type === 'open_ended' && (
                <div className="open-ended-evaluation-section">
                  <h6>🤖 Évaluation Automatique</h6>
                  
                  {/* Statut de configuration */}
                  {/* <div className={`evaluation-status ${evaluationStatus?.configured ? 'status-active' : 'status-created'}`}>
                    <span className="status-badge">
                      {evaluationStatus?.configured ? '✅ Configuré' : '❌ Non configuré'}
                    </span>
                    <p className="status-message">{evaluationStatus?.message}</p>
                  </div> */}

                  {/* Statistiques d'évaluation */}
                  {evaluationStats && (
                    <div className="evaluation-stats">
                      <div className="stats-summary">
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats.evaluatedResponses}</div>
                          <div className="stat-label">Évaluées</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats.unevaluatedResponses}</div>
                          <div className="stat-label">Non évaluées</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats.averageScore.toFixed(1)}%</div>
                          <div className="stat-label">Score moyen</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-number">{evaluationStats.evaluationRate}%</div>
                          <div className="stat-label">Taux d'évaluation</div>
                        </div>
                      </div>

                      {/* Distribution des scores */}
                      <div className="score-distribution">
                        <h6>Distribution des scores</h6>
                        <div className="distribution-chart">
                          {[
                            { label: 'Excellent (90-100%)', count: evaluationStats.scoreDistribution.excellent, color: '#28a745' },
                            { label: 'Bon (70-89%)', count: evaluationStats.scoreDistribution.good, color: '#17a2b8' },
                            { label: 'Moyen (50-69%)', count: evaluationStats.scoreDistribution.average, color: '#ffc107' },
                            { label: 'Insuffisant (0-49%)', count: evaluationStats.scoreDistribution.poor, color: '#dc3545' }
                          ].map(({ label, count, color }) => {
                            const percentage = evaluationStats.evaluatedResponses > 0 ? (count / evaluationStats.evaluatedResponses) * 100 : 0;
                            return (
                              <div key={label} className="distribution-item">
                                <div className="distribution-label">{label}</div>
                                <div className="distribution-bar">
                                  <div 
                                    className="distribution-fill" 
                                    style={{ 
                                      width: `${percentage}%`,
                                      backgroundColor: color
                                    }}
                                  />
                                </div>
                                <div className="distribution-count">{count}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Actions d'évaluation */}
                      {evaluationStats.unevaluatedResponses > 0 && (
                        <div className="evaluation-actions">
                          <button
                            onClick={() => handleEvaluateAllResponses(session.currentQuestion!.id)}
                            className="btn btn-primary btn-sm"
                            disabled={evaluatingQuestions.has(session.currentQuestion!.id)}
                          >
                            {evaluatingQuestions.has(session.currentQuestion!.id) ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Évaluation en cours...
                              </>
                            ) : (
                              `Évaluer ${evaluationStats.unevaluatedResponses} réponse(s) restante(s)`
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Questions de la Session</h3>
          </div>
          <div className="card-body">
            {questions.length === 0 ? (
              <p className="text-center text-muted">
                Aucune question dans cette session.
                <br />
                <Link
                  to={`/teacher/sessions/${session.id}/questions/create`}
                  className="btn btn-primary btn-sm mt-2"
                >
                  Créer la première question
                </Link>
              </p>
            ) : (
              <div className="questions-list">
                {questions.map((question: Question) => (
                  <div key={question.id} className={`question-item ${session.currentQuestion && session.currentQuestion.id === question.id ? 'question-active' : ''}`}>
                    <div className="question-header">
                      <div className="question-title-row">
                        <h4>{question.title}</h4>
                        {session.currentQuestion && session.currentQuestion.id === question.id && (
                          <span className="active-indicator">
                            🔴 ACTIVE
                          </span>
                        )}
                      </div>
                      <span className={`question-type ${question.type}`}>
                        {question.type === 'multiple_choice' && 'QCM'}
                        {question.type === 'investigation' && "Investigation"}
                        {question.type === 'open_ended' && 'Question ouverte'}
                        {question.type === 'true_false' && 'Vrai/Faux'}
                        {question.type === 'rating' && 'Évaluation'}
                      </span>
                    </div>
                    <p className="question-content">{question.content}</p>
                    
                    {/* Affichage des réponses */}
                    {question.responses && question.responses.length > 0 && (
                      <div className="question-responses">
                        <div className="responses-header" style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '0.5rem 0',
                          borderBottom: '1px solid #e9ecef',
                          marginBottom: '1rem'
                        }}
                        onClick={() => setShowResponses(!showResponses)}
                        >
                          <h5 style={{ margin: 0 }}>Réponses ({question.responses.length})</h5>
                          <span style={{ 
                            fontSize: '1.2em',
                            transition: 'transform 0.3s ease',
                            transform: showResponses ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}>
                            ▼
                          </span>
                        </div>
                        {showResponses && (
                          <div className="responses-list">
                          {question.responses.map((response: any, index: number) => {
                            // Détecter si c'est une réponse auto-soumise (toujours "Pas de réponse")
                            const isAutoSubmitted = response.answer === '[Pas de réponse - Temps écoulé]';
                            
                            return (
                              <div key={index} className="response-item" style={{
                                border: isAutoSubmitted ? '2px solid #ffeaa7' : '1px solid #e9ecef',
                                background: isAutoSubmitted ? '#fff3cd' : 'white'
                              }}>
                                <div className="response-header">
                                  <span className="student-name">
                                    {response.studentName}
                                    {isAutoSubmitted && (
                                      <span style={{ 
                                        fontSize: '0.7em', 
                                        color: '#856404', 
                                        marginLeft: '0.5rem',
                                        background: '#ffeaa7',
                                        padding: '2px 6px',
                                        borderRadius: '10px'
                                      }}>
                                        ⏰ Auto-soumise
                                      </span>
                                    )}
                                  </span>
                                  <div className="response-meta">
                                    <span className="response-time">
                                      {new Date(response.timestamp).toLocaleTimeString()}
                                    </span>
                                    {response.modificationCount !== undefined && response.modificationCount > 0 && (
                                      <span className="modification-count">
                                        {response.modificationCount} modification{response.modificationCount > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="response-answer">
                                  {isAutoSubmitted ? (
                                    // Affichage spécial pour les réponses auto-soumises
                                    <span className="answer-text" style={{
                                      background: '#fff3cd',
                                      border: '1px solid #ffeaa7',
                                      color: '#856404',
                                      fontWeight: '500'
                                    }}>
                                      {response.answer}
                                      <span style={{ fontSize: '0.8em', color: '#856404', marginLeft: '0.5rem' }}>
                                        ⏰ (Générée automatiquement)
                                      </span>
                                    </span>
                      ) : question.type === 'rating' ? (
                        // Affichage spécial pour les questions rating
                        <span className="rating-response-badge" style={{
                          padding: '0.5rem 1rem',
                          background: '#e3f2fd',
                          color: '#1565c0',
                          border: '2px solid #bbdefb',
                          borderRadius: '8px',
                          fontWeight: '500'
                        }}>
                          {question.ratingScale?.labels?.[parseInt(response.answer) - question.ratingScale.min] || response.answer}
                        </span>
                      ) : question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'investigation' ? (
                        // Affichage normal pour les réponses QCM/Vrai-Faux et investigations
                        <span
                          className={`answer-badge${
                            question.type === 'investigation' || !question.correctAnswer
                              ? ''
                              : response.answer === question.correctAnswer
                                ? ' correct'
                                : ' incorrect'
                          }`}
                        >
                          {question.options && question.options[parseInt(response.answer)] ? 
                            question.options[parseInt(response.answer)].text : 
                            `Option ${response.answer}`
                          }
                        </span>
                      ) : question.type === 'open_ended' ? (
                        // Affichage pour les questions ouvertes avec évaluation
                        <div className="open-ended-response">
                          <div className="student-answer">
                            <span className="answer-text" style={{
                              display: 'block',
                              padding: '0.75rem',
                              background: '#f8f9fa',
                              border: '1px solid #e9ecef',
                              borderRadius: '4px',
                              fontStyle: 'italic',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {response.answer}
                            </span>
                          </div>
                          
                          {/* Affichage de l'évaluation si disponible */}
                          {response.concordanceScore !== null && response.concordanceScore !== undefined ? (
                            <div className="evaluation-display" style={{
                              marginTop: '0.75rem',
                              padding: '0.75rem',
                              background: '#e7f3ff',
                              border: '1px solid #17a2b8',
                              borderRadius: '4px',
                              borderLeft: '4px solid #17a2b8'
                            }}>
                              <div className="evaluation-header" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '0.5rem'
                              }}>
                                <span className={`score-badge ${
                                  response.concordanceScore >= 90 ? 'score-excellent' :
                                  response.concordanceScore >= 70 ? 'score-good' :
                                  response.concordanceScore >= 50 ? 'score-average' : 'score-poor'
                                }`} style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.85rem',
                                  fontWeight: '600'
                                }}>
                                  {response.concordanceScore}%
                                </span>
                                <span className="score-label" style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '500',
                                  color: '#495057'
                                }}>
                                  {response.concordanceScore >= 90 ? 'Excellent' :
                                   response.concordanceScore >= 70 ? 'Bon' :
                                   response.concordanceScore >= 50 ? 'Moyen' : 'Insuffisant'}
                                </span>
                                {response.evaluationTimestamp && (
                                  <small style={{
                                    color: '#6c757d',
                                    fontSize: '0.8rem',
                                    marginLeft: 'auto'
                                  }}>
                                    Évalué le {new Date(response.evaluationTimestamp).toLocaleString()}
                                  </small>
                                )}
                              </div>
                              
                              {response.aiFeedback && (
                                <div className="ai-feedback" style={{
                                  marginTop: '0.5rem'
                                }}>
                                  <strong style={{ color: '#495057', fontSize: '0.9rem' }}>Feedback :</strong>
                                  <div style={{
                                    margin: '0.25rem 0 0 0',
                                    fontSize: '0.9rem',
                                    color: '#495057'
                                  }}>
                                    <MarkdownFeedback content={response.aiFeedback} />
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="unevaluated-display" style={{
                              marginTop: '0.75rem',
                              padding: '0.5rem',
                              background: '#fff3cd',
                              border: '1px solid #ffeaa7',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              <span style={{
                                fontSize: '0.85rem',
                                color: '#856404',
                                fontStyle: 'italic'
                              }}>
                                ⏳ Réponse non évaluée
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Affichage normal pour les autres types
                        <span className="answer-text" style={{
                          background: 'rgba(248, 249, 250, 0.8)',
                          border: '1px solid #e9ecef'
                        }}>
                          {response.answer}
                        </span>
                      )}
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="question-actions">
                      <button
                        onClick={() => activateQuestion(question.id)}
                        className="btn btn-sm btn-primary"
                      >
                        Activer
                      </button>
                      <Link
                        to={`/teacher/sessions/${session.id}/questions/${question.id}/edit`}
                        className="btn btn-sm btn-outline"
                      >
                        Éditer
                      </Link>
                      <button
                        onClick={() => handleDeleteQuestion(question.id, question.title)}
                        className="btn btn-sm"
                        style={{ 
                          background: '#dc3545', 
                          borderColor: '#dc3545', 
                          color: 'white' 
                        }}
                        title="Supprimer cette question"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                    
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      {/* Modal des statistiques */}
      {showStatistics && (
        <div className="modal-overlay" onClick={() => setShowStatistics(false)}>
          <div className="modal stats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Statistiques de la Session</h3>
              <button 
                className="btn-close" 
                onClick={() => setShowStatistics(false)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <TeacherSessionStats 
                session={session}
                questions={questions}
                sessionStats={sessionStats}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Modal du classement */}
      {showRanking && (
        <div className="modal-overlay" onClick={() => setShowRanking(false)}>
          <div className="modal ranking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🏆 Classement des Étudiants</h3>
              <div className="modal-header-actions">
                <button 
                  className="btn btn-success btn-sm"
                  onClick={() => exportRankingToCSV()}
                  title="Exporter le classement en CSV"
                >
                  📊 Export CSV
                </button>
                <button 
                  className="btn-close" 
                  onClick={() => setShowRanking(false)}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="modal-content">
              <StudentRanking 
                session={session}
                questions={questions}
              />
            </div>
          </div>
        </div>
      )}
      </main>


      {/* Modal d'import de questions */}
      {showImportModal && session && (
        <ImportQuestions
          sessionId={session.id}
          onImportSuccess={handleImportSuccess}
          onCancel={() => setShowImportModal(false)}
        />
      )}

      {/* Modal de partage */}
      {showShareModal && session && (
        <ShareSessionModal
          sessionId={session.id}
          sessionName={session.name}
          currentSharedWith={session.sharedWith}
          onClose={() => setShowShareModal(false)}
          onUpdate={() => {
            loadSessionData(true);
            setShowShareModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TeacherSessionManagement;
